
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    /* eslint-disable indent */
import { ANALYTICS_EVENTS, useSendAnalyticsEvent } from 'api/analytics';
import { ArtistMediumCount, getArtist, getArtistMediums, getArtistsCount, } from 'api/artists';
import { getArtistArtworks } from 'api/artworks';
import { useAvailableAuction } from 'api/auctions';
import classNames from 'classnames';
import { FormikSubmitValueChangeListener } from 'components/abstract/FormikSubmitValueChangeListener';
import { ArtistSections } from 'components/artists/ArtistSections';
import { ArtistSummary } from 'components/artists/ArtistSummary';
import { ArtworkCard } from 'components/artwork/ArtworkCard';
import { AvailableAuctionBanner } from 'components/common/AvailableAuctionBanner';
import { AvailableAuctionSection } from 'components/common/AvailableAuctionSection';
import { Breadcrumbs } from 'components/common/Breadcrumbs';
import { Button } from 'components/common/Button';
import { Icon } from 'components/common/Icon';
import { NaturalImage } from 'components/common/NaturalImage';
import { Option } from 'components/common/Option';
import { Select } from 'components/common/Select';
import { Tab } from 'components/common/Tab';
import { Tabs } from 'components/common/Tabs';
import routes from 'constants/routes';
import { Form, Formik } from 'formik';
import { useAutomaticShowAuthModal } from 'hooks/useAutomaticShowAuthModal';
import { useSaveSearchedIds } from 'hooks/useSaveSearchedIds';
import { BaseLayout } from 'layouts/BaseLayout';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { useRouter } from 'next/router';
import qs from 'qs';
import { ParsedUrlQueryInput } from 'querystring';
import { ComponentProps, Fragment, ReactElement, useCallback, useEffect, useMemo, useState, } from 'react';
import { useInfiniteQuery } from 'react-query';
import { range } from 'utils/arrays';
import { buildArtistSchemaLd, buildArtworksSchemaLds, buildBreacrumbsSchemaLd, } from 'utils/artist-page';
import { mapIArtistToArtistSummary, mapIArtworksToArtworkCards, } from 'utils/data';
import { getArtistImageUrl } from 'utils/image';
import { restoreScroll, saveScroll } from 'utils/scroll';
import { getArtistsPaths, getArtistUrl } from 'utils/urls';
const SHOWN_REGISTER_ARTIST = 'shown_register_artist';
const ArtworksLoader = ({ count }: {
    count: number;
}) => {
    return (<>
      {range(0, count).map(i => (<ArtworkCard key={i}/>))}
    </>);
};
type Props = {
    id: number;
    initialData: {
        artist: ComponentProps<typeof ArtistSummary>;
        mediums: ArtistMediumCount[];
    };
    url?: string;
};
const Artist = ({ id, initialData: { artist, mediums }, url }: Props) => {
    const { t } = useTranslation();
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(true);
    const { sendAnalyticsEvent } = useSendAnalyticsEvent();
    const { data: infiniteArtworks, fetchNextPage, hasNextPage, isFetchedAfterMount, isFetching, } = useInfiniteQuery(['artworks', router.query], async ({ pageParam = 1 }) => {
        return await fetchArtworks({
            id,
            pageQuery: router.query,
            page: pageParam
        });
    }, {
        refetchOnWindowFocus: false,
        keepPreviousData: false,
        getNextPageParam: ({ pagination }) => {
            if (pagination.page < pagination.pageCount) {
                return pagination.page + 1;
            }
            return undefined;
        }
    });
    const { availableAuction } = useAvailableAuction();
    const artistHasAuction = availableAuction?.artistId == artist.id;
    const artworksLds = useMemo(() => {
        const schemaLds = infiniteArtworks?.pages.flatMap(page => buildArtworksSchemaLds(page.artworks, t)) || [];
        return schemaLds;
    }, [t, infiniteArtworks]);
    const artistLd = buildArtistSchemaLd(artist);
    const breadcrumbsLd = buildBreacrumbsSchemaLd(artist);
    useSaveSearchedIds(id, {
        entities: 'artist',
        key: 'ArtistPage-searched-artists'
    });
    useAutomaticShowAuthModal({
        id,
        shouldDisplay: useCallback(() => {
            try {
                const shownRegister = localStorage.getItem(SHOWN_REGISTER_ARTIST);
                return !shownRegister || shownRegister !== 'yes';
            }
            catch (err) {
                console.error(err);
                return false;
            }
        }, []),
        afterDisplay: useCallback(() => {
            localStorage.setItem(SHOWN_REGISTER_ARTIST, 'yes');
        }, [])
    });
    useEffect(() => {
        if (!isFetchedAfterMount)
            return;
        restoreScroll('artist').then(() => {
            setIsLoading(false);
        });
    }, [isFetchedAfterMount]);
    useEffect(() => {
        if (artist)
            sendAnalyticsEvent({
                eventName: ANALYTICS_EVENTS.VIEW_ARTIST,
                artistId: String(artist.id),
                artistName: artist.name
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artist]);
    if (!artist)
        return null;
    const pageTitle = t('single_artist:page_title', {
        artistName: artist.name,
        artworksCount: mediums?.[0]?.count ?? 0
    });
    const pageDescription = t('single_artist:page_desc', {
        artistName: artist.name
    });
    const currentTotalArtworks = infiniteArtworks?.pages.reduce((prev, page) => prev + page.artworks.length, 0) || 0;
    return (<>
      <Head>
        <title>{pageTitle}</title>
        <meta name='description' content={pageDescription}/>

        <meta name='og:title' content={pageTitle}/>
        <meta name='og:description' content={pageDescription}/>
        <meta name='og:image' content={getArtistImageUrl(artist.name, artist.profile, 'thumbnail')}/>

        <link rel='canonical' href={`${process.env.NEXT_PUBLIC_SITE_URL}${getArtistUrl(artist.name, artist.id)}`}/>

        {artworksLds &&
            artworksLds.map((schema, i) => (<script key={schema.name} type='application/ld+json' id={`product-jsonld-${i}`} dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}/>))}

        {artistLd && (<script type='application/ld+json' id='artist-jsonld' dangerouslySetInnerHTML={{ __html: JSON.stringify(artistLd) }}/>)}

        {breadcrumbsLd && (<script type='application/ld+json' id='breadcrumb-jsonld' dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsLd) }}/>)}
      </Head>

      {artistHasAuction && <AvailableAuctionBanner variant='sticky'/>}

      <div>
        <div className='container border-y border-cool-300 md:border-0'>
          <Breadcrumbs url={url} className='!my-4 sm:!my-4 md:!my-6'/>
        </div>

        {artist && (<div className='container'>
            <ArtistSummary {...artist}/>
          </div>)}

        {artistHasAuction && <AvailableAuctionSection className='md:mt-6'/>}

        <Formik enableReinitialize initialValues={{
            medium: (router?.query?.medium as string) || 'All',
            sort: (router?.query?.sort as string) || 'Recommended'
        }} onSubmit={async ({ medium, sort }, { setSubmitting }) => {
            router.push({ query: { ...router.query, medium, sort } }, undefined, {
                scroll: false,
                shallow: true
            });
            setSubmitting(false);
        }}>
          {({ values: { medium: currentMedium, sort }, setFieldValue }) => (<Form className={classNames('container sticky z-30 flex space-x-4 bg-cool-50 pb-6 pt-4 sm:py-8', artistHasAuction
                ? 'top-28 md:top-24 lg:top-32 [@media(max-width:425px)]:top-36'
                : 'top-16')}>
              <div className='w-1/2 lg:w-full'>
                <div className='hidden lg:block'>
                  <Tabs activeKey={currentMedium} onChange={value => setFieldValue('medium', value)}>
                    {mediums?.map((medium, index) => {
                const { label, count } = medium;
                const slicedLabel = label.endsWith('s')
                    ? label.slice(0, label.length - 1)
                    : label;
                const isAllTab = label.toLowerCase() === 'all';
                return (<Tab key={index} valueKey={slicedLabel} as={isAllTab ? 'h2' : 'h3'}>
                          {isAllTab
                        ? t('single_artist:all_artist_works', {
                            artistName: artist.name
                        })
                        : label}{' '}
                          {currentMedium === slicedLabel && <>({count})</>}
                        </Tab>);
            })}
                  </Tabs>
                </div>

                <Select className='lg:hidden' value={currentMedium} onChange={value => setFieldValue('medium', value)}>
                  {mediums?.map(({ label }, index) => (<Option key={index} value={label.endsWith('s')
                    ? label.slice(0, label.length - 1)
                    : label}>
                      {label}
                    </Option>))}
                </Select>
              </div>

              <div className='w-1/2 lg:w-max lg:min-w-[220px]'>
                <Select before={`${t('common:sort_by')} `} value={sort} onChange={value => setFieldValue('sort', value)}>
                  <Option value='Recommended'>{t('common:recommended')}</Option>
                  <Option value='Lowest Ask'>{t('common:lowest_ask')}</Option>
                  <Option value='Highest Bid'>{t('common:highest_bid')}</Option>
                  <Option value='Release date: Newest first'>
                    {t('common:newest_first')}
                  </Option>
                  <Option value='Release date: Oldest first'>
                    {t('common:oldest_first')}
                  </Option>
                  <Option value='Latest Offers'>
                    {t('common:latest_offers')}
                  </Option>
                  <Option value='Latest Listings'>
                    {t('common:latest_listings')}
                  </Option>
                </Select>
              </div>

              <FormikSubmitValueChangeListener debounceTime={30}/>
            </Form>)}
        </Formik>

        <div className='mb-20'>
          <div className='container grid grid-cols-2 gap-x-4 gap-y-16 sm:grid-cols-3  md:grid-cols-4  lg:grid-cols-5 xl:grid-cols-6'>
            {!infiniteArtworks && <ArtworksLoader count={60}/>}

            {isLoading && <ArtworksLoader count={currentTotalArtworks}/>}

            {!isLoading &&
            infiniteArtworks &&
            infiniteArtworks?.pages?.map((page, pageIndex) => {
                return page.artworks.map((artwork, artworkIndex) => {
                    const artworkProps = {
                        ...artwork,
                        onLinkClick: () => saveScroll({ redirectedFrom: 'artist' })
                    };
                    // on mobile, only the first two artwork are visible on page load
                    // because of that, we want to fetch the images with priority so they render as fast as possible
                    // the other images can render when needed, they won't affect metric like LCP
                    if (pageIndex === 0 &&
                        (artworkIndex === 0 || artworkIndex === 1)) {
                        return (<Fragment key={artwork.id}>
                        <ArtworkCard {...artworkProps} priority className='sm:hidden'/>

                        <ArtworkCard {...artworkProps} className='hidden sm:block'/>
                      </Fragment>);
                    }
                    return <ArtworkCard key={artwork.id} {...artworkProps}/>;
                });
            })}
          </div>

          {hasNextPage && (<Button className='mx-auto mt-16' pill size='lg' onClick={fetchNextPage} disabled={isFetching}>
              <span className='mr-3'>{t('single_artist:load_more')}</span>
              <Icon name='arrow-down'/>
            </Button>)}
        </div>

        {mediums?.[0]?.count === 0 && (<div className='container'>
            <div className='flex w-full flex-col items-center justify-center rounded border border-cool-300 bg-cool-50 px-8 py-12 text-center'>
              <NaturalImage src='/files/not-found.svg' width={150} height={150} alt='not found'/>
              <p>{t('single_artist:sorry_no_results')}</p>
            </div>
          </div>)}

        <ArtistSections sections={artist.sections}/>
      </div>
    </>);
};
Artist.getLayout = (page: ReactElement) => (<BaseLayout style='pure-white'>{page}</BaseLayout>);
const fetchData = async (id: number) => {
    const query = qs.stringify({
        populate: {
            images: {
                fields: ['name', 'url', 'formats']
            },
            nationalities: {
                fields: ['alpha2Code', 'name']
            },
            socialMedia: {
                fields: '*'
            },
            sections: {
                fields: '*'
            }
        }
    }, {
        encodeValuesOnly: true
    });
    const artistData = await getArtist(id, query);
    const artist = mapIArtistToArtistSummary(artistData.data);
    const { mediums } = await getArtistMediums(id);
    return {
        artist,
        mediums
    };
};
interface FetchArtworksPayload {
    id: number;
    pageQuery: ParsedUrlQueryInput;
    page: number;
    pageSize?: number;
}
const fetchArtworks = async ({ id, pageQuery, page, pageSize = 60, }: FetchArtworksPayload) => {
    if (!id)
        return {
            artworks: [],
            pagination: { page: 1, pageCount: 1, pageSize: 0 }
        };
    const { medium, sort: sortBy = 'Recommended' } = pageQuery;
    const artworks = await getArtistArtworks(id, page, pageSize, sortBy as string, medium as string);
    return {
        artworks: mapIArtworksToArtworkCards(artworks.data),
        pagination: artworks.meta.pagination
    };
};
const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
    const slug = params?.slug;
    if (!slug) {
        return { notFound: true, props: { error: 'NO SLUG' } };
    }
    const id = Number(slug[slug.length - 1]);
    if (isNaN(id)) {
        return { notFound: true, props: { error: 'INVALID ID' } };
    }
    try {
        const { artist, mediums } = await fetchData(id);
        if (!artist) {
            return { notFound: true, props: { error: 'NO ARTIST' } };
        }
        return {
            props: {
                id,
                initialData: {
                    artist,
                    mediums: mediums || []
                },
                url: typeof slug !== 'string' ? `${routes.artists}/${slug.join('/')}` : ''
            }
        };
    }
    catch (err) {
        console.log(err);
        return { notFound: true, props: { error: (err as Error).message, err } };
    }
};
export const getStaticPaths: GetStaticPaths = async () => {
    const paths: GetStaticPathsResult['paths'] = [];
    try {
        const totalArtists = await getArtistsCount();
        while (paths.length < totalArtists) {
            const page = Math.ceil(paths.length / 100) || 1;
            const artistsPaths = await getArtistsPaths({
                startFromPage: page,
                limit: 100
            });
            paths.push(...artistsPaths);
        }
    }
    catch (err) {
        console.log(err);
    }
    return { paths, fallback: 'blocking' };
};
export default Artist;

    async function __Next_Translate__getStaticProps__19cd7554fcb__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/artists/[...slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19cd7554fcb__ as getStaticProps }
  